<template>
  <div
    class="is-flex is-flex-direction-column has-gap-12"
    :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_ESCALATION_CARD"
  >
    <div class="clara-escalation-card is-flex is-radius-medium is-overflow-hidden has-border-light">
      <div class="clara-escalation-accent is-flex-shrink-0 has-width-4" />

      <div class="is-flex is-flex-direction-column has-gap-8 p-3">
        <p class="is-uppercase is-size-6 has-text-weight-semibold has-text-primary">
          {{ $t('ai.clara-for-planners.escalation-label') }}
        </p>

        <p class="whitespace-pre-line">{{ $t('ai.clara-for-planners.escalation-body') }}</p>

        <div class="has-border-top-light p-t-8">
          <p class="is-size-6 has-text-weight-bold has-text-grey">
            {{ $t('ai.clara-for-planners.escalation-footnote') }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="!selectedAction" class="is-flex is-align-items-center has-gap-8">
      <ButtonView
        type="is-primary"
        size="is-small"
        :disabled="!jurisdictionId"
        :data-e2e="AICypressAttribute.buttons.CLARA_FOR_PLANNERS_ESCALATION_BOOK"
        @click="onBookClick"
      >
        {{ $t('ai.clara-for-planners.escalation-book') }}
      </ButtonView>

      <ButtonView
        type="is-ghost"
        size="is-small"
        class="clara-escalation-compare"
        :data-e2e="AICypressAttribute.buttons.CLARA_FOR_PLANNERS_ESCALATION_COMPARE"
        @click="onCompareClick"
      >
        {{ $t('ai.clara-for-planners.escalation-compare') }}
      </ButtonView>
    </div>

    <template v-else>
      <div class="is-flex is-justify-content-flex-end">
        <p
          class="has-background-primary-light p-3 is-radius-large"
          :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_ESCALATION_REQUEST"
        >
          {{ requestLabel }}
        </p>
      </div>

      <p :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_ESCALATION_CONFIRMATION">
        {{ confirmationMessage }}
      </p>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import ModalService from '@/modules/core/ui/services/ModalService';
import MeetingFreeConsultModalService from '@/modules/meeting/services/ui/MeetingFreeConsultModalService';
import SelfServeUpgradeModal from '@/modules/billing/components/checkout/stateful/SelfServeUpgradeModal.vue';
import { getters } from '@/modules/core/app/helpers/store';
import { ClaraForPlannersEscalationAction } from '@/modules/ai/constants/ClaraForPlannersEscalationAction';

export default defineComponent({
  name: 'ClaraForPlannersEscalationCard',
  components: {
    ButtonView
  },
  static: {
    AICypressAttribute
  },
  data(): {
    selectedAction: ClaraForPlannersEscalationAction | null;
  } {
    return {
      selectedAction: null
    };
  },
  computed: {
    jurisdictionId(): number | undefined {
      return getters.auth.getUser()?.currentEstate?.jurisdictionId;
    },
    isBookSelected(): boolean {
      return this.selectedAction === ClaraForPlannersEscalationAction.BOOK;
    },
    requestLabel(): string {
      return this.isBookSelected
        ? this.$t('ai.clara-for-planners.escalation-book')
        : this.$t('ai.clara-for-planners.escalation-compare');
    },
    confirmationMessage(): string {
      return this.isBookSelected
        ? this.$t('ai.clara-for-planners.booking-page-opened')
        : this.$t('ai.clara-for-planners.pricing-page-opened');
    }
  },
  methods: {
    onBookClick(): void {
      if (!this.jurisdictionId) {
        return;
      }

      this.selectedAction = ClaraForPlannersEscalationAction.BOOK;
      MeetingFreeConsultModalService.show(this, this.jurisdictionId);
    },
    onCompareClick(): void {
      this.selectedAction = ClaraForPlannersEscalationAction.COMPARE;
      ModalService.show({
        parent: this,
        component: SelfServeUpgradeModal,
        fullScreen: true
      });
    }
  }
});
</script>

<style scoped>
.clara-escalation-card {
  background: var(--ce-white-bis);
}

.clara-escalation-accent {
  background: var(--ce-primary);
}

.clara-escalation-compare,
.clara-escalation-compare:hover,
.clara-escalation-compare:focus {
  text-decoration: none;
}
</style>
